/**
 * streamExtract.js
 *
 * Tolerant partial-JSON scanner for the streaming dialogue path. The model
 * responds with a JSON body (responseMimeType application/json); while it
 * streams, only the value of the top-level "dialogue" string field should
 * reach the terminal. This extractor consumes raw text chunks split at
 * arbitrary byte boundaries (mid-key, mid-escape, mid \uXXXX) and emits
 * decoded dialogue text increments, never structural tokens and never content
 * from any other field.
 *
 * Matching is token-level: a string token is treated as the dialogue key only
 * when the complete token equals "dialogue" AND the next non-whitespace
 * character is ':'. A string VALUE that happens to equal "dialogue"
 * (e.g. "emotional_state":"dialogue") fails the colon check and is skipped.
 * Only the first matching key streams; everything after its closing quote is
 * ignored.
 */

const ESCAPE_MAP = {
  '"': '"',
  '\\': '\\',
  '/': '/',
  b: '\b',
  f: '\f',
  n: '\n',
  r: '\r',
  t: '\t',
};

const HEX_RE = /^[0-9a-fA-F]$/;

/**
 * Create a streaming dialogue extractor.
 * @param {(text: string) => void} emit  Called with each decoded increment of
 *   the dialogue string value (possibly one call per push, batched).
 * @returns {{ push(chunk: string): void, end(): void, done(): boolean }}
 */
export function createDialogueExtractor(emit) {
  // States:
  //   scan     outside any string token
  //   str      inside a string token (key or non-dialogue value)
  //   strEsc   after backslash inside a string token
  //   afterKey string token equal to "dialogue" just closed; seeking ':'
  //   seekVal  ':' consumed; seeking the opening quote of the value
  //   val      inside the dialogue value; emitting
  //   valEsc   after backslash inside the dialogue value
  //   valU     collecting 4 hex digits of \uXXXX inside the dialogue value
  //   done     dialogue value fully consumed; ignore the rest
  let state = 'scan';
  let token = '';   // current string token text (raw, for key comparison)
  let hex = '';     // accumulating \uXXXX digits

  function push(chunk) {
    if (!chunk || state === 'done') return;
    let out = '';
    let i = 0;
    while (i < chunk.length) {
      const ch = chunk[i];
      switch (state) {
        case 'scan':
          if (ch === '"') { state = 'str'; token = ''; }
          i += 1;
          break;
        case 'str':
          if (ch === '\\') { state = 'strEsc'; token += ch; }
          else if (ch === '"') { state = token === 'dialogue' ? 'afterKey' : 'scan'; }
          else { token += ch; }
          i += 1;
          break;
        case 'strEsc':
          // Raw append; an escaped token can never equal the bare key
          // "dialogue", so decoding is unnecessary for the comparison.
          token += ch;
          state = 'str';
          i += 1;
          break;
        case 'afterKey':
          if (ch === ':') { state = 'seekVal'; i += 1; }
          else if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { i += 1; }
          else {
            // The "dialogue" token was a value, not a key. Reprocess this
            // character from scan state.
            state = 'scan';
          }
          break;
        case 'seekVal':
          if (ch === '"') { state = 'val'; i += 1; }
          else if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { i += 1; }
          else {
            // Non-string value (null, number...). Nothing to stream; resume
            // scanning so a later real key could still match.
            state = 'scan';
          }
          break;
        case 'val':
          if (ch === '\\') { state = 'valEsc'; }
          else if (ch === '"') { state = 'done'; }
          else { out += ch; }
          i += 1;
          break;
        case 'valEsc':
          if (ch === 'u') { state = 'valU'; hex = ''; }
          else {
            out += Object.prototype.hasOwnProperty.call(ESCAPE_MAP, ch) ? ESCAPE_MAP[ch] : ch;
            state = 'val';
          }
          i += 1;
          break;
        case 'valU':
          if (HEX_RE.test(ch)) {
            hex += ch;
            if (hex.length === 4) {
              out += String.fromCharCode(parseInt(hex, 16));
              state = 'val';
            }
          } else {
            // Malformed escape; tolerate by resuming the value verbatim and
            // reprocessing this character.
            state = 'val';
          }
          if (state === 'valU' || HEX_RE.test(ch)) i += 1;
          break;
        case 'done':
          i = chunk.length;
          break;
        default:
          i += 1;
      }
      if (state === 'done') break;
    }
    if (out && typeof emit === 'function') emit(out);
  }

  function end() { /* no buffered lookahead; nothing to flush */ }

  function done() { return state === 'done'; }

  return { push, end, done };
}
